import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
// The previous asset was an automotive body-in-white line with robot arms, which
// contradicted the bottling story and the BBCorp/Lisbon/Packaging namespace.
// factoryBeerMedium is a 600x900 re-encode of the supplied factory-beer.jpg
// (1333x2000, 1.8MB): this slot renders ~300px wide, so the original would have
// shipped 15x the bytes for the browser to downscale anyway. The original is kept
// in assets/illustrations if a larger crop is ever needed.
import factoryImage from '~/assets/illustrations/factoryBeerMedium.jpeg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/factory/')({ component: FactoryDemo })

function FactoryDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Factory" subtitle="Digital transformation for smart manufacturing" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              objectPosition="center"
              maxW={{ base: '100%', md: '300px' }}
              maxH={{ base: '260px', md: 'unset' }}
              src={factoryImage}
              alt="Bottling line - capped bottles leaving the capper"
            />
            <Stack flex={1} mb="6">
              <Card.Header>
                <Heading size="lg" color="content.primary">
                  Driving Operational Excellence with Real-Time Data
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4} mb="4">
                  <Text>
                    BBCorp faced a critical challenge: siloed production data across legacy OPC-UA
                    and Modbus TCP systems was creating blind spots, slowing decision-making, and
                    leaving millions in operational savings untapped.
                  </Text>
                  <Text>
                    By deploying the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , BBCorp transformed their manufacturing operations with a unified, real-time
                    data infrastructure that delivers measurable business impact.
                  </Text>
                  <Text>
                    <Link
                      href="https://www.hivemq.com/products/hivemq-edge/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Edge
                    </Link>{' '}
                    seamlessly bridges legacy equipment to modern systems, while the enterprise
                    broker ensures reliable data flow across all plants - enabling instant
                    visibility from shop floor to executive dashboard.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>15% increase in OEE</b> through real-time visibility into production
                          bottlenecks
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>40% reduction in unplanned downtime</b> with predictive maintenance
                          powered by live equipment data
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Faster time-to-insight</b> by eliminating data silos and enabling
                          enterprise-wide analytics
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Future-proof architecture</b> that scales from single line to global
                          multi-site deployments
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  OPC-UA and Modbus TCP equipment streaming production data in real time via MQTT
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Raw machine signals normalized into governed KPIs across all production lines
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  OEE, downtime, and quality metrics analyzed in real time from line to enterprise
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Predictive maintenance and quality deviations trigger corrective actions before
                  losses occur
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Lisbon packaging: the live end-to-end scenario */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Lisbon Packaging, End to End"
            description="Two packaging lines running the same SKU, where a single slow machine drags the line down and the loudest symptoms appear on the wrong machines."
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/line' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Live Line</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Live performance for both lines, per-machine states and buffer levels, plus the
                  degradation controls for presenters.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/line' })
                  }}
                >
                  View Live Line
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Browse the governed namespace as the Edge publishes it, from the enterprise root
                  down to a single machine tag.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/explorer' })
                  }}
                >
                  Explore Namespace
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flows */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flows" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Simple Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Explore the factory data flow architecture for a single production line.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/advancedflow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Advanced Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Multi-edge factory setup with ML at the edge.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/advancedflow' })
                  }}
                >
                  View Advanced
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/multisiteflow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Multi-Site Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Enterprise-wide deployment across multiple factory sites.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/multisiteflow' })
                  }}
                >
                  View Multi-Site
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/explorer' })
                  }}
                >
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/bot' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">AI Assistant</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Chat with AI about factory operations.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/bot' })
                  }}
                >
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/factory/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Real-time network visualization of the Unified Namespace.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/factory/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
